import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdmin } from '@/auth/AuthProvider';
import { ArchiveIcon, RefreshIcon } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Notice } from '@/components/ui/Notice';
import { PageHeader } from '@/components/ui/PageHeader';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { useArchivedJobs, type JobView } from '@/hooks/useActiveJobs';
import { formatJobDate } from '@/lib/format';
import { errorMessage } from '@/services/apiError';
import { jobsApi } from '@/services/jobsApi';

export function ArchivePage() {
  const jobs = useArchivedJobs();
  const [search, setSearch] = useState('');
  const term = search.trim();
  const list = jobs.state === 'ready' ? jobs.data.filter((j) => !term || j.name.includes(term) || j.location.includes(term)) : [];

  return (
    <>
      <PageHeader title="ארכיון" subtitle="עבודות שהסתיימו. עבודה עוברת לכאן אוטומטית יום אחרי התאריך שלה." />

      {jobs.state === 'ready' && jobs.data.length > 0 && (
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="חיפוש לפי שם או מיקום"
          aria-label="חיפוש בארכיון"
          className="mb-4 block min-h-12 w-full rounded-control border border-line-strong bg-surface px-3.5 text-[16px] placeholder:text-ink-3 focus:border-brand focus:outline-none md:max-w-sm"
        />
      )}

      {jobs.state === 'loading' && (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      )}
      {jobs.state === 'error' && <Notice tone="error">{jobs.message}</Notice>}
      {jobs.state === 'ready' && jobs.data.length === 0 && (
        <EmptyState icon={<ArchiveIcon size={30} />} title="הארכיון ריק" body="עבודות שהסתיימו יעברו לכאן אוטומטית, ויישארו זמינות לצפייה ולהפקת דוחות." />
      )}
      {jobs.state === 'ready' && jobs.data.length > 0 && list.length === 0 && <p className="py-8 text-center text-ink-2">לא נמצאו עבודות תואמות</p>}

      <ul className="space-y-3">
        {list.map((job) => (
          <ArchivedRow key={job.id} job={job} />
        ))}
      </ul>
    </>
  );
}

function ArchivedRow({ job }: { job: JobView }) {
  const { user } = useAdmin();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  return (
    <li className="flex items-center gap-3 rounded-card border border-line bg-surface p-4">
      <Link to={`/jobs/${job.id}`} className="min-w-0 flex-1">
        <h3 className="truncate text-[16px] font-semibold">{job.name}</h3>
        <p className="mt-0.5 text-[14px] text-ink-2">{formatJobDate(job.date)}</p>
        <p className="mt-0.5 truncate text-[13px] text-ink-3">
          {job.location} · {job.totalWorkers} עובדים
        </p>
      </Link>
      <Button
        variant="secondary"
        className="min-h-11 shrink-0 px-3 text-[14px]"
        icon={<RefreshIcon size={17} />}
        loading={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await jobsApi(user).unarchive(job.id);
            toast('העבודה הוחזרה לפעילות');
          } catch (err) {
            toast(errorMessage(err), 'error');
            setBusy(false);
          }
        }}
      >
        <span className="hidden sm:inline">החזרה לפעילות</span>
        <span className="sm:hidden">החזרה</span>
      </Button>
    </li>
  );
}
